// A ported subset of the real GUI's own statistics/frequency-analysis
// dialog: variable-length events only (not daily/monthly/annual grouping),
// Mean/Peak/Total/Duration only, Weibull plotting position only. "Total"
// (event volume) is only offered for a FLOW-LIKE variable — this app has no
// per-node flow series to integrate a node Depth/Head event's volume from,
// so it's left unavailable rather than silently computing a meaningless 0.

export type StatsType = "mean" | "peak" | "total" | "duration";

interface StatsEventRow {
  rank: number;
  startTime: Date;
  duration: number; // hours
  value: number;
  frequencyPercent: number; // exceedance %, Weibull: 100*rank/(N+1)
  returnPeriod: number; // years or months, Weibull: (duration+1)/rank
}

interface StatsSummary {
  eventCount: number;
  eventFrequency: number; // fraction of ticks that were part of an accepted event
  min: number;
  max: number;
  mean: number;
  stdDev: number;
  skew: number;
  periodYearsOrMonths: number;
  periodUnit: "years" | "months";
}

interface StatisticsReport {
  summary: StatsSummary;
  events: StatsEventRow[];
}

/** Under 3 years of data, report in months instead — a return period in years reads as nonsense over a few months of simulated data. */
function findPeriod(times: Date[]): { value: number; unit: "years" | "months" } {
  if (times.length < 2) return { value: 1, unit: "months" };
  const days = (times[times.length - 1].getTime() - times[0].getTime()) / 86400000;
  const years = Math.floor(days / 365 + 0.5);
  if (years >= 3) return { value: years, unit: "years" };
  const months = Math.max(1, Math.floor(days / (365 / 12) + 0.5));
  return { value: months, unit: "months" };
}

/** `flows` is `null` for a non-flow-like variable (node Depth/Head) — Total and `minEventVolume` are then ignored, every event accepted regardless of its own uncomputed volume. `times` is assumed uniformly spaced. */
export function computeStatisticsReport(
  times: Date[],
  values: number[],
  flows: number[] | null,
  minEventValue: number,
  minEventVolume: number,
  minEventDeltaHours: number,
  statsType: StatsType
): StatisticsReport {
  const n = values.length;
  const tickHours = n > 1 ? (times[1].getTime() - times[0].getTime()) / 3600000 : 1;

  interface RawEvent {
    startIdx: number;
    duration: number;
    value: number;
  }
  const raw: RawEvent[] = [];

  let wetStartIdx = 0;
  let wetEndIdx = 0;
  let dryEndIdx = 0;
  let nWet = 0;
  let ySum = 0;
  let yMax = 0;
  let vSum = 0;
  let eventPeriods = 0;

  function closeEventIfDue() {
    if (nWet === 0) return;
    const dryHours = (dryEndIdx - wetEndIdx) * tickHours;
    if (dryHours < minEventDeltaHours) return;
    if (flows === null || vSum > minEventVolume) {
      const durationHours = (wetEndIdx - wetStartIdx) * tickHours;
      let value: number;
      switch (statsType) {
        case "mean":
          value = ySum / nWet;
          break;
        case "peak":
          value = yMax;
          break;
        case "total":
          value = vSum;
          break;
        case "duration":
          value = durationHours;
          break;
      }
      raw.push({ startIdx: wetStartIdx, duration: durationHours, value });
      eventPeriods += wetEndIdx - wetStartIdx;
    }
    nWet = 0;
    ySum = 0;
    yMax = 0;
    vSum = 0;
  }

  for (let t = 0; t < n; t++) {
    closeEventIfDue();
    const y = values[t];
    const q = flows ? flows[t] : y;
    dryEndIdx = t + 1;
    if (y > minEventValue) {
      const absY = Math.abs(y);
      const absQ = Math.abs(q);
      vSum += absQ * tickHours;
      if (nWet === 0) wetStartIdx = t;
      nWet++;
      wetEndIdx = t + 1;
      ySum += absY;
      if (absY > yMax) yMax = absY;
    }
  }
  // Force-close whatever's still pending at the end of the series,
  // regardless of dry-time.
  dryEndIdx = wetEndIdx + Math.ceil(minEventDeltaHours / tickHours) + 1;
  closeEventIfDue();

  const totalPeriods = Math.max(1, n);
  const period = findPeriod(times);

  // Rank descending by value; ties share the higher (smaller-index) rank —
  // real source's own `RankEvents`.
  const sorted = [...raw].sort((a, b) => b.value - a.value);
  const ranks = new Array<number>(sorted.length);
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (i === sorted.length - 1) ranks[i] = sorted.length;
    else ranks[i] = sorted[i].value === sorted[i + 1].value ? ranks[i + 1] : i + 1;
  }

  const events: StatsEventRow[] = sorted.map((e, i) => {
    const rank = ranks[i];
    return {
      rank,
      startTime: times[e.startIdx],
      duration: e.duration,
      value: e.value,
      frequencyPercent: (100 * rank) / (sorted.length + 1),
      returnPeriod: (period.value + 1) / rank,
    };
  });

  const count = events.length;
  let mean = 0;
  let stdDev = 0;
  let skew = 0;
  let min = 0;
  let max = 0;
  if (count > 0) {
    max = events[0].value;
    min = events[count - 1].value;
    let a1 = 0;
    let a2 = 0;
    let a3 = 0;
    for (const e of events) {
      a1 += e.value;
      a2 += e.value ** 2;
      a3 += e.value ** 3;
    }
    mean = a1 / count;
    const varNumerator = Math.max(0, a2 - (a1 * a1) / count);
    stdDev = Math.sqrt(varNumerator / (count > 1 ? count - 1 : count));
    const t1 = a3 - (3 * a1 * a2) / count + (2 / count / count) * a1 ** 3;
    let t2 = a2 / count - (a1 / count) ** 2;
    t2 = t2 > 0 ? t2 ** 1.5 : 0;
    skew = t2 !== 0 ? t1 / t2 / count : 0;
  }

  return {
    summary: {
      eventCount: count,
      eventFrequency: eventPeriods / totalPeriods,
      min,
      max,
      mean,
      stdDev,
      skew,
      periodYearsOrMonths: period.value,
      periodUnit: period.unit,
    },
    events,
  };
}
